import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, FormGroup, Input, Label, Spinner } from 'reactstrap';
import BZAPIService from '../../../services/BZAPIService';
import {
  fleetSummary,
  matchingVersionChoice,
  sameVersion,
  moduleOptions,
  versionGroupProjectName,
  versionGroups
} from './inventorySummary';

/**
 * Turns a scan result into a project.
 *
 * A project is never created without a confirmed version: when the backend
 * cannot match the fleet's version to an ODD release it returns
 * created:false and writes nothing, and we ask the user to pick.
 */
const InventoryConfirmStep = ({
  snapshot,
  managedProductId,
  managedProductName,
  discoveredProductName,
  discoveredProductId,
  // The Plan product the managed product declares (its snProductId), when the
  // vendor sync writes one. Vendor-neutral: absent for F5, whose devices are
  // not catalog products; present for non-modular vendors (STRY0038595).
  declaredProductId,
  vendorId,
  allProducts,
  // The version a ServiceNow CI-record deep link asked for. Pre-selected over
  // the majority when the fleet reports it; ignored otherwise.
  preferredVersion,
  onCreated,
  onCancel
}) => {
  // STRY0038566 (Plan-only inventory discovery): a discovered target has no
  // managed product row to consult. discoveredProductName is the CMDB-scanned
  // identity — byte-identical to the `productName` used to start this fetch
  // job — and is sent back only so the create endpoint can verify ownership
  // (create_project_from_inventory.py's job_vendor_product matches it against
  // the fetch job's SFN input `productName`). It is a scan identity, not an
  // ODD product: productId is chosen the same way as the managed-product
  // path, from the module picker below, and is what actually gets resolved
  // against ODD and persisted.
  const isDiscoveredMode = managedProductId == null && Boolean(discoveredProductName);
  const summary = useMemo(() => fleetSummary(snapshot), [snapshot]);
  // In discovered (Plan-only) mode the user already picked a specific product,
  // so the ODD id to analyze is fixed (discoveredProductId) — NOT chosen from a
  // fallback module list, which defaulted to an unrelated product. Fall back to
  // the display name if the id is somehow absent: the create endpoint then
  // returns unresolved and the version picker appears, still for the right
  // product. The managed-product path uses the snapshot/declared-product
  // module list (STRY0038595).
  const modules = useMemo(
    () =>
      isDiscoveredMode
        ? [{ value: discoveredProductId || discoveredProductName, label: discoveredProductName }]
        : moduleOptions(snapshot, allProducts, declaredProductId),
    [isDiscoveredMode, discoveredProductId, discoveredProductName, snapshot, allProducts, declaredProductId]
  );

  const groups = useMemo(() => versionGroups(snapshot), [snapshot]);
  // The majority group is pre-selected: it is what the old flow created, so
  // doing nothing but pressing Create still yields the same project. A deep
  // link's requested version wins when the fleet has a group for it.
  const [selectedVersions, setSelectedVersions] = useState(() => {
    const g = versionGroups(snapshot);
    const preferred = preferredVersion && g.find(x => sameVersion(x.version, preferredVersion));
    if (preferred) return [preferred.version];
    const majority = (snapshot || {}).majorityVersion;
    if (majority && g.some(x => x.version === majority)) return [majority];
    return g.length ? [g[0].version] : [];
  });
  const [productId, setProductId] = useState(modules.length ? modules[0].value : '');
  const [versionId, setVersionId] = useState('');
  const [versionChoices, setVersionChoices] = useState([]);
  const [unresolved, setUnresolved] = useState(false);
  const [showVersionPicker, setShowVersionPicker] = useState(false);
  // GH #815: the unresolved fallback must never be a dead end. When the
  // product the backend tried is not in the vendor catalog (or the catalog has
  // no releases for it), the reviewer picks the product here and a release of
  // that product; the create call then sends it as an explicit override.
  const [triedProductId, setTriedProductId] = useState(null);
  // Which half of the resolve failed: 'product' (not in the catalog) or
  // 'releases' (in the catalog, but no releases to choose from).
  const [unresolvedCause, setUnresolvedCause] = useState(null);
  // The inventory version that failed to resolve — the selected group's, which
  // is not necessarily the majority (GH #822).
  const [unresolvedVersion, setUnresolvedVersion] = useState(null);
  const [loadingChoices, setLoadingChoices] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [overrideProductId, setOverrideProductId] = useState('');
  // Only the latest release lookup may write versionChoices: switching product
  // while a slower lookup is in flight must not show the old product's list.
  const choicesRequestRef = useRef(0);
  const [name, setName] = useState('');
  const [nameEditedManually, setNameEditedManually] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // A fleet where nothing matched this product in the CMDB. Nothing else in
  // the confirm step (module picker, version, create) is meaningful here —
  // the failure table calls for a specific message, not the generic
  // unresolved-version path (which would otherwise trigger once the user
  // hits Create, inviting them to baseline a fleet with no matching devices).
  const isEmptyFleet = summary.majorityVersion === null;

  // The device's modules. No longer offered as a choice — they seed productId
  // and union the release list across the whole device. Applies the same way
  // in discovered mode: there is no managed-product vendorData to consult, but
  // moduleOptions() already falls back to allProducts when the scan carries no
  // componentProfile, so the seeding/union logic needs no special case here.
  const moduleChoices = modules;

  // Named for the device and the version group being reviewed. The module is
  // deliberately absent: a project is a device plus a version, and putting one
  // of the device's modules in the name is what made these read as "F5 PEM"
  // instead of "the i4600 fleet".
  useEffect(() => {
    if (nameEditedManually) return;
    setName(versionGroupProjectName(managedProductName, selectedVersions[0] || summary.majorityVersion));
  }, [managedProductName, selectedVersions, summary.majorityVersion, nameEditedManually]);

  // productId is no longer chosen by a human, but it still has to be a module
  // this device actually has: the create endpoint uses it to disambiguate, and
  // a stale value from a previous scan would name a module the new device does
  // not run.
  useEffect(() => {
    if (!moduleChoices.length) return;
    if (!moduleChoices.some(m => m.value === productId)) {
      setProductId(moduleChoices[0].value);
    }
    // productId is intentionally excluded: this effect reacts to the
    // available options changing, not to the selection itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleChoices]);

  /**
   * Releases offered when the fleet's own version cannot be resolved.
   *
   * Unioned across the device's modules, for the same reason the bug query is:
   * a release the device can run is a release any of its modules publishes,
   * and asking only the module we happened to pick would hide the rest. The
   * modules are queried in parallel and de-duplicated by suggestion id.
   */
  const loadVersionChoices = async (productIds = null) => {
    const ids = productIds || (moduleChoices.length ? moduleChoices.map(m => m.value) : [productId].filter(Boolean));
    const request = ++choicesRequestRef.current;
    setLoadingChoices(true);
    try {
      const responses = await Promise.all(
        // A module with no releases must not sink the whole list, so each
        // request fails to an empty result rather than rejecting the batch.
        ids.map(id => BZAPIService.bsaGetBstSoftwareSuggestions(vendorId, id).catch(() => null))
      );
      const seen = new Set();
      const merged = [];
      responses.forEach(r => {
        ((r && r.results) || []).forEach(item => {
          if (item && item.suggestionId && !seen.has(item.suggestionId)) {
            seen.add(item.suggestionId);
            merged.push(item);
          }
        });
      });
      // suggestionId stays the value submitted as versionId; label with the
      // human-readable release, matching the wizard's own version picker
      // (BugScrubTool.js fetchVersions). Falls back to the id if a release
      // string isn't present.
      const choices = merged.map(r => ({ value: r.suggestionId, label: r.releaseVersion || r.suggestionId }));
      if (request === choicesRequestRef.current) setVersionChoices(choices);
      return choices;
    } catch (e) {
      if (request === choicesRequestRef.current) setVersionChoices([]);
      return [];
    } finally {
      if (request === choicesRequestRef.current) setLoadingChoices(false);
    }
  };

  // Whether a product id is one of the device's modules — the ids the release
  // lookups union across. A product outside them (a declared id the catalog
  // does not carry, whose modules then fell back to the whole catalog) must
  // never be rescued from that union: it fans out one request per catalog
  // entry and can "find" the release under an unrelated product.
  const isModule = id => moduleChoices.some(m => m.value === id);

  // The managed product's own declared product, when the catalog does not
  // carry it. The backend always resolves against it, so only an explicit
  // product pick can get this fleet a project.
  const declaredUncatalogued =
    !isDiscoveredMode &&
    Boolean(declaredProductId) &&
    (allProducts || []).length > 0 &&
    !(allProducts || []).some(p => p && p.value === declaredProductId);

  const catalogLabel = id => ((allProducts || []).find(p => p && p.value === id) || {}).label || id;

  /**
   * The backend would not create the project: it could not resolve the fleet's
   * version against the product it tried. Work out which half failed.
   *
   * - The product is not in the vendor catalog (a device model the catalog
   *   does not carry, or a mis-derived id): asking for a release of it can
   *   only ever show an empty list, so go straight to the product picker.
   * - The product is known but has no releases: same — offer another product.
   * - Otherwise the version is the problem: offer the product's releases, with
   *   the product picker one click away in case the product is wrong too.
   */
  const enterUnresolved = async created => {
    const tried = (created && created.productId) || productId;
    // A product the reviewer picked did not resolve the fleet's version: ask
    // for the version of that product, not for a product again.
    if (overrideProductId) {
      setProductPickerOpen(false);
      setUnresolvedCause(null);
    }
    setUnresolvedVersion((created && created.inventoryVersion) || selectedVersions[0] || summary.majorityVersion);
    setUnresolved(true);
    setVersionId('');
    setTriedProductId(tried);
    const catalog = allProducts || [];
    // Known = something the release lookup below can actually ask about: one
    // of the device's modules (F5's come from the scan's component profile and
    // need not appear in the catalog list) or a catalog entry. A declared id
    // that is neither would only yield an empty list — or, when the modules
    // fell back to the whole catalog, other products' releases.
    const inModules = isModule(tried);
    const productKnown = !catalog.length || inModules || catalog.some(p => p && p.value === tried);
    if (!productKnown) {
      setUnresolvedCause('product');
      setVersionChoices([]);
      setProductPickerOpen(true);
      return;
    }
    // A catalog product that is not one of the modules gets its own list.
    const choices = await loadVersionChoices(inModules || !tried ? null : [tried]);
    if (!choices.length && catalog.length) {
      setUnresolvedCause('releases');
      setProductPickerOpen(true);
    }
  };

  const chooseOverrideProduct = async id => {
    setOverrideProductId(id);
    setVersionId('');
    if (!id || productOnly) {
      choicesRequestRef.current += 1;
      setVersionChoices([]);
      setLoadingChoices(false);
      return;
    }
    const request = choicesRequestRef.current + 1;
    const choices = await loadVersionChoices([id]);
    // Preselect the release the fleet reports, in the catalog's spelling
    // (STRY0038794); the reviewer can still change it. Skipped when another
    // product was picked meanwhile.
    const match = matchingVersionChoice(choices, unresolvedVersion || selectedVersions[0] || summary.majorityVersion);
    if (match && request === choicesRequestRef.current) setVersionId(match.value);
  };

  // Undeclared managed product: the scan already knows the fleet's version,
  // so only the product is asked for and Create resolves the version against
  // it. The version list appears only if that resolve fails (enterUnresolved).
  const productOnly = unresolvedCause === 'undeclared' && !unresolved;

  const openProductPicker = () => {
    setProductPickerOpen(true);
    setOverrideProductId('');
    setVersionId('');
    choicesRequestRef.current += 1;
    setVersionChoices([]);
    setLoadingChoices(false);
  };

  // [change] next to the version: same picker the unresolved path uses, but
  // reached deliberately rather than because resolution failed.
  const openVersionPicker = async () => {
    setShowVersionPicker(true);
    // Releases of other products would be offered for a product the catalog
    // does not carry — ask for the product first.
    if (declaredUncatalogued) {
      setTriedProductId(declaredProductId);
      setUnresolvedCause('product');
      openProductPicker();
      return;
    }
    await loadVersionChoices();
  };

  // The managed product declares no Plan product yet (no vendor sync since
  // the deploy), so the backend refuses the productId this screen defaulted
  // to (STRY0038794). Ask the reviewer to pick the product on purpose.
  // The picker creates one project, so a multi-version selection narrows to
  // its first group.
  const enterUndeclared = () => {
    const version = selectedVersions[0] || summary.majorityVersion;
    if (selectedVersions.length > 1) setSelectedVersions(selectedVersions.slice(0, 1));
    setUnresolvedVersion(version);
    setShowVersionPicker(true);
    setTriedProductId('');
    setUnresolvedCause('undeclared');
    openProductPicker();
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    const body = { fetchJobId: snapshot.fetchJobId, name, productId: overrideProductId || productId };
    // A product the reviewer picked from the catalog replaces the managed
    // product's own (unresolvable) one — see productOverride in
    // create_project_from_inventory.py.
    if (overrideProductId) body.productOverride = true;
    // Exactly one of managedProductId or {vendorId, productName} — see
    // CreateFromInventoryBody in create_project_from_inventory.py, which
    // rejects a body carrying both (or neither). productName is the scan
    // identity used only for the endpoint's ownership check; productId is the
    // ODD product, sent unconditionally above.
    if (isDiscoveredMode) {
      body.vendorId = vendorId;
      body.productName = discoveredProductName;
    } else {
      body.managedProductId = managedProductId;
    }
    if (versionId) body.versionId = versionId;
    // The fleet string the baseline stands for, so drift later compares the
    // fleet's form with the fleet's form rather than with the release id.
    // The selected group's, not always the majority (GH #822): the backend
    // resolves this when no versionId is sent, and baselines drift on it.
    const inventoryVersion = selectedVersions[0] || summary.majorityVersion;
    if (inventoryVersion) body.inventoryVersion = inventoryVersion;
    try {
      const created = await BZAPIService.bsaInvCreateProjectFromInventory(body);
      if (created && created.created === false) {
        setSaving(false);
        await enterUnresolved(created);
        return;
      }
      setSaving(false);
      onCreated(created);
    } catch (e) {
      setSaving(false);
      if (e && e.reason === 'undeclared_product' && !overrideProductId) {
        enterUndeclared();
        return;
      }
      setError('Could not create the project. Please try again.');
    }
  };

  /**
   * One project per selected version group.
   *
   * Created in sequence rather than in parallel: the failure report has to say
   * exactly which versions did not make it, and a half-finished batch that
   * cannot name its casualties is worse than a slower one that can.
   */
  /**
   * Releases across the device's modules, keyed by release string.
   *
   * Used to rescue a version the backend could not resolve. Resolution there
   * goes through productId — a single module — and productId is now seeded
   * automatically, so a fleet on a perfectly real release can fail simply
   * because the seeded module never shipped it. The union knows better, and
   * asking the reviewer to pick a version we can already identify is a
   * question with an obvious answer.
   */
  const resolveVersionId = async version => {
    const ids = moduleChoices.length ? moduleChoices.map(m => m.value) : [productId].filter(Boolean);
    const responses = await Promise.all(
      ids.map(id => BZAPIService.bsaGetBstSoftwareSuggestions(vendorId, id).catch(() => null))
    );
    for (const r of responses) {
      for (const item of (r && r.results) || []) {
        if (item && (sameVersion(item.releaseVersion, version) || sameVersion(item.suggestionId, version))) {
          return item.suggestionId;
        }
      }
    }
    return null;
  };

  const submitGroups = async () => {
    setSaving(true);
    setError(null);
    const chosen = groups.filter(g => selectedVersions.includes(g.version));
    const made = [];
    const failed = [];
    let lastUnresolved = null;
    let undeclared = false;
    for (const group of chosen) {
      try {
        const body = {
          fetchJobId: snapshot.fetchJobId,
          productId,
          name: versionGroupProjectName(managedProductName, group.version),
          inventoryVersion: group.version
        };
        // Exactly one of managedProductId or {vendorId, productName} — see
        // submit() above.
        if (isDiscoveredMode) {
          body.vendorId = vendorId;
          body.productName = discoveredProductName;
        } else {
          body.managedProductId = managedProductId;
        }
        // Every group — majority or not — is left for the backend to resolve
        // from inventoryVersion. Sending the raw CMDB version as versionId
        // assumed it equals an ODD suggestion id: true for F5, not for ESXi,
        // where "7.0.0 build-16324942" was persisted as-is and the project
        // came out with no current version and nothing to review (GH #822).
        let created = await BZAPIService.bsaInvCreateProjectFromInventory(body);
        // created:false is the backend refusing to guess a version it cannot
        // resolve to a known release — a real outcome, not an exception. Before
        // surfacing it, try to resolve the release ourselves across every
        // module the device runs: the backend only looked at the seeded one.
        // Only when the product the backend tried is one of the device's
        // modules: otherwise the union is other products' releases.
        if (created && created.created === false && (!created.productId || isModule(created.productId))) {
          const resolved = await resolveVersionId(group.version);
          if (resolved) {
            created = await BZAPIService.bsaInvCreateProjectFromInventory({ ...body, versionId: resolved });
          }
        }
        if (created && created.created === false) {
          failed.push(group.version);
          lastUnresolved = created;
        } else made.push(created);
      } catch (e) {
        if (e && e.reason === 'undeclared_product') {
          undeclared = true;
          break; // every group would be refused the same way
        }
        failed.push(group.version);
      }
    }
    setSaving(false);

    if (undeclared && !made.length) {
      enterUndeclared();
      return;
    }

    // One group that would not resolve is the original single-project case:
    // hand the reviewer the version picker to recover with, rather than a
    // sentence telling them what went wrong and no way to fix it.
    if (failed.length === 1 && !made.length && chosen.length === 1) {
      await enterUnresolved(lastUnresolved);
      return;
    }

    if (failed.length) {
      setError(
        made.length
          ? `Created ${made.length} project${made.length === 1 ? '' : 's'}. Could not match ${failed.join(
              ', '
            )} to a known release — create ${
              failed.length === 1 ? 'it' : 'those'
            } individually to pick the closest version.`
          : `Could not match ${failed.join(', ')} to a known release.`
      );
    }
    // Single-argument, as before: the caller opens the project it is handed
    // and refetches the list. Widening this signature for the batch case would
    // change a contract every caller relies on for the sake of a count.
    if (made.length) onCreated(made[made.length - 1]);
  };

  if (isEmptyFleet) {
    return (
      <div className="inventory-confirm">
        <h5>{managedProductName}</h5>
        <Alert color="warning">No devices matched this product in the CMDB — check the CI query.</Alert>
        <Button color="secondary" outline onClick={onCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="inventory-confirm">
      <h5>{managedProductName}</h5>
      <p>
        {summary.totalCis} device{summary.totalCis === 1 ? '' : 's'} in your inventory
        {groups.length > 1 && <> across {groups.length} versions</>} &mdash; choose which to review.{' '}
        <Button color="link" size="sm" className="p-0 align-baseline" onClick={openVersionPicker}>
          pick a different version
        </Button>
      </p>
      {/* One project per group: a project reviews upgrade candidates from a
          single current version, so groups cannot be merged into one. */}
      <FormGroup>
        <div className="d-flex justify-content-between align-items-baseline">
          <Label className="mb-1">Inventory versions</Label>
          {groups.length > 1 && (
            <Button
              color="link"
              size="sm"
              className="p-0"
              onClick={() =>
                setSelectedVersions(selectedVersions.length === groups.length ? [] : groups.map(g => g.version))
              }
            >
              {selectedVersions.length === groups.length ? 'Clear all' : 'Select all'}
            </Button>
          )}
        </div>
        {groups.map(group => (
          <FormGroup check key={group.version}>
            <Input
              id={`inv-group-${group.version}`}
              type="checkbox"
              checked={selectedVersions.includes(group.version)}
              onChange={() =>
                setSelectedVersions(prev =>
                  prev.includes(group.version) ? prev.filter(v => v !== group.version) : [...prev, group.version]
                )
              }
            />
            <Label check for={`inv-group-${group.version}`}>
              {group.ciCount} device{group.ciCount === 1 ? '' : 's'} &mdash; <strong>{group.version}</strong>
            </Label>
          </FormGroup>
        ))}
        {selectedVersions.length > 1 && (
          <div className="text-muted small mt-1">Creates {selectedVersions.length} projects, one per version.</div>
        )}
      </FormGroup>
      {/* The module picker used to live here. A project is a device plus a
          version, and asking which of the device's 29 modules it is "about"
          was a question with no answer in the customer's world — their CIs are
          appliances, not modules. productId is still sent, chosen as the
          fleet's most-used module, because the create endpoint uses it to
          disambiguate; nothing the reviewer sees depends on it any more. */}
      {(unresolved || showVersionPicker) && (
        <FormGroup>
          {unresolved && !productPickerOpen && (
            <Alert color="warning">
              Your inventory reports {unresolvedVersion || summary.majorityVersion}, which does not match a known
              release. Choose the closest one.
            </Alert>
          )}
          {productPickerOpen && (
            <Alert color="warning">
              {unresolvedCause === 'product' && (
                <>
                  We couldn&rsquo;t match {managedProductName}
                  {triedProductId ? <> ({triedProductId})</> : null} to a product in the catalog, so its{' '}
                  {unresolvedVersion || summary.majorityVersion} release can&rsquo;t be looked up.{' '}
                </>
              )}
              {unresolvedCause === 'undeclared' && (
                <>We couldn&rsquo;t tell which Plan catalog product {managedProductName} is from its name. </>
              )}
              {unresolved && unresolvedCause === 'releases' && (
                <>
                  The catalog has no releases for {catalogLabel(triedProductId)}, so{' '}
                  {unresolvedVersion || summary.majorityVersion} can&rsquo;t be matched.{' '}
                </>
              )}
              {(allProducts || []).length
                ? productOnly
                  ? `Choose the catalog product to review; its ${unresolvedVersion ||
                      summary.majorityVersion} release is looked up when you create the project.`
                  : 'Choose the product to review, then its version.'
                : 'The product catalog is unavailable right now — set the project up manually instead.'}
            </Alert>
          )}
          {productPickerOpen && (allProducts || []).length > 0 && (
            <>
              <Label for="inv-product">Choose a product</Label>
              <Input
                id="inv-product"
                type="select"
                className="mb-2"
                value={overrideProductId}
                onChange={e => chooseOverrideProduct(e.target.value)}
              >
                <option value="">Select&hellip;</option>
                {(allProducts || []).map(p => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Input>
            </>
          )}
          {(!productPickerOpen || overrideProductId) && !productOnly && (
            <>
              <Label for="inv-version">Choose a version</Label>
              <Input
                id="inv-version"
                type="select"
                value={versionId}
                disabled={loadingChoices}
                onChange={e => setVersionId(e.target.value)}
              >
                <option value="">{loadingChoices ? 'Loading…' : 'Select…'}</option>
                {versionChoices.map(v => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </Input>
              {!loadingChoices && !versionChoices.length && (
                <div className="text-muted small mt-1">
                  No releases are catalogued for {catalogLabel(overrideProductId || triedProductId || productId)}.
                </div>
              )}
            </>
          )}
          {!productPickerOpen && (allProducts || []).length > 0 && (
            <Button color="link" size="sm" className="p-0 mt-1" onClick={openProductPicker}>
              Not the right product? Choose another
            </Button>
          )}
          {productPickerOpen && !(allProducts || []).length && (
            <Button color="link" size="sm" className="p-0" onClick={onCancel}>
              Set up manually
            </Button>
          )}
        </FormGroup>
      )}
      {/* A single name only makes sense for a single project. For a batch the
          names are generated per version, and offering one box to name them
          all would promise something we cannot deliver. */}
      {selectedVersions.length <= 1 && (
        <FormGroup>
          <Label for="inv-name">Project name</Label>
          <Input
            id="inv-name"
            value={name}
            maxLength={128}
            onChange={e => {
              setNameEditedManually(true);
              setName(e.target.value);
            }}
          />
        </FormGroup>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      <Button
        color="primary"
        onClick={selectedVersions.length > 1 || (!unresolved && !showVersionPicker) ? submitGroups : submit}
        disabled={
          saving ||
          !productId ||
          ((unresolved || productPickerOpen) && !versionId && !productOnly) ||
          (productPickerOpen && !overrideProductId) ||
          !selectedVersions.length
        }
      >
        {saving ? (
          <Spinner size="sm" />
        ) : selectedVersions.length > 1 ? (
          `Create ${selectedVersions.length} projects`
        ) : (
          'Create project'
        )}
      </Button>{' '}
      <Button color="secondary" outline onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
};

export default InventoryConfirmStep;
